import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ColorFacetSwatch from '@loomstateorg/shared-frontend/components/product-color-swatches/ColorFacetSwatch';

export type SelectedFilterVariant = 'text' | 'color';

export interface SelectedFilterItem {
  id: string;
  facetLabel: string;
  valueLabel: string;
  onRemove: () => void;
  variant?: SelectedFilterVariant;
  colorKey?: string;
  hexColor?: string;
}

export interface SidebarSelectedFiltersProps {
  items: SelectedFilterItem[];
  onClearAll?: () => void;
  appliedFiltersLabel?: string;
  clearAllLabel?: string;
}

const CHIP_SWATCH_SIZE = 'size-14';
const CHIP_SWATCH_SLOT = 'size-16';

/** Fixed space for chip padding, gap, and remove button (px-10 + gap-6 + size-14 button). */
const CHIP_FIXED_WIDTH = '2.875rem';

const getLongestTextLength = (items: SelectedFilterItem[]): number =>
  Math.max(
    2,
    ...items.map((item) => (item.variant === 'color' ? 0 : item.valueLabel.length)),
  );

interface SelectedFilterChipProps {
  item: SelectedFilterItem;
  contentLength: number;
  index?: number;
}

const SelectedFilterChip: React.FC<SelectedFilterChipProps> = ({ item, contentLength, index = 0 }) => {
  const isColor = item.variant === 'color' && item.colorKey;

  return (
    <li>
      <div
        style={{ width: `calc(${contentLength}ch + ${CHIP_FIXED_WIDTH})` }}
        className="flex h-28 items-center justify-center gap-6 rounded-full border border-neutral-400 bg-inherit px-10"
        data-testid={`sidebar-selected-filter-chip-${item.id}`}
      >
        {isColor ? (
          <ColorFacetSwatch
            colorKey={item.colorKey!}
            hexColor={item.hexColor}
            sizeClassName={CHIP_SWATCH_SIZE}
            slotClassName={CHIP_SWATCH_SLOT}
          />
        ) : (
          <span
            className="font-primaryBold text-14 leading-none text-gray-700"
            data-testid={`selected-filter-value-${index}`}
          >
            {item.valueLabel}
          </span>
        )}
        <button
          type="button"
          onClick={item.onRemove}
          className="inline-flex size-14 shrink-0 items-center justify-center bg-inherit text-gray-700 transition hover:opacity-60"
          aria-label={`Remove ${item.facetLabel}: ${item.valueLabel}`}
          data-testid={`sidebar-selected-filter-remove-${item.id}`}
        >
          <XMarkIcon className="size-10 stroke-[2.5]" aria-hidden="true" />
        </button>
      </div>
    </li>
  );
};

const SidebarSelectedFilters: React.FC<SidebarSelectedFiltersProps> = ({
  items,
  onClearAll,
  appliedFiltersLabel = 'Applied filters',
  clearAllLabel = 'Clear All',
}) => {
  if (!items.length) return null;

  const contentLength = getLongestTextLength(items);

  return (
    <section className="pb-10" aria-label={appliedFiltersLabel} data-testid="sidebar-selected-filters">
      <h2 className="mb-8 font-primaryBold text-14 uppercase tracking-wide">{appliedFiltersLabel}</h2>
      <ul
        className="grid grid-cols-[repeat(3,max-content)] gap-x-8 gap-y-10 pl-8 lg:pl-10 2xl:pl-12"
        role="list"
        data-testid="selected-filter-list"
      >
        {items.map((item, index) => (
          <SelectedFilterChip key={item.id} item={item} contentLength={contentLength} index={index} />
        ))}
      </ul>
      {onClearAll && (
        <button
          type="button"
          className="mt-16 pl-8 font-primaryBold text-12 uppercase tracking-wide text-gray-700 underline transition hover:opacity-60 lg:pl-10 2xl:pl-12"
          onClick={onClearAll}
          data-testid="sidebar-clear-all-filters"
        >
          {clearAllLabel}
        </button>
      )}
    </section>
  );
};

export default SidebarSelectedFilters;
